import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import {
  ArrowLeft,
  Search,
  QrCode,
  User,
  MapPin,
  Calendar as CalendarIcon,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Package,
  Plus,
  X,
} from 'lucide-react';
import { checkInOutService } from '@/services/checkinout-service';
import { EvoFeatureFeedbackCollector } from '@/components/FeedbackCollector';
import type { CheckOutRequest, AccessoryItem } from '@/types/checkinout';
import type { Asset } from '@/types/asset';
import { format, addDays } from 'date-fns';
import { toast } from 'sonner';

// Helper function to get step label - extracted to fix nested ternary (S3358)
function getStepLabel(step: number): string {
  if (step === 1) {
    return 'Select Asset';
  }
  if (step === 2) {
    return 'Assignment Details';
  }
  return 'Review & Confirm';
}

export default function CheckOutAsset(): React.ReactElement {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [availableAssets, setAvailableAssets] = useState<Asset[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [sites, setSites] = useState<any[]>([]);
  
  // Form state
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [assignedTo, setAssignedTo] = useState('');
  const [assignedToType, setAssignedToType] = useState<'user' | 'team'>('user');
  const [location, setLocation] = useState('');
  const [locationType, setLocationType] = useState<'site' | 'remote'>('site');
  const [expectedReturnDate, setExpectedReturnDate] = useState<Date>(addDays(new Date(), 7));
  const [notes, setNotes] = useState('');
  const [accessories, setAccessories] = useState<Omit<AccessoryItem, 'id' | 'returned'>[]>([]);
  const [newAccessory, setNewAccessory] = useState('');
  const [complianceChecks, setComplianceChecks] = useState<any[]>([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      searchAssets();
    }
  }, [searchTerm]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      // TODO: Implement API calls when service is ready
      setAvailableAssets([]);
      setUsers([]);
      setSites([]);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load initial data');
    } finally {
      setLoading(false);
    }
  };

  const searchAssets = async () => {
    try {
      // TODO: Implement search when service is ready
      setAvailableAssets([]);
    } catch (error) {
      console.error('Search failed:', error);
    }
  };

  const handleAssetSelect = async (asset: Asset) => {
    setSelectedAsset(asset);
    setStep(2);
    
    // Load compliance checks for this asset
    try {
      // TODO: Implement compliance checks when service is ready
      setComplianceChecks([]);
    } catch (error) {
      console.error('Failed to load compliance checks:', error);
    }
  };

  const handleAddAccessory = () => {
    if (newAccessory.trim()) {
      setAccessories([...accessories, { name: newAccessory.trim(), quantity: 1 }]);
      setNewAccessory('');
    }
  };

  const handleRemoveAccessory = (index: number) => {
    setAccessories(accessories.filter((_, i) => i !== index));
  };

  const getComplianceStatus = () => {
    if (complianceChecks.length === 0) return 'ok';
    const hasBlocking = complianceChecks.some(c => c.status === 'fail' && c.blocking);
    const hasWarnings = complianceChecks.some(c => c.status === 'warning');
    if (hasBlocking) return 'blocked';
    if (hasWarnings) return 'warning';
    return 'ok';
  };

  const canProceed = () => {
    const complianceStatus = getComplianceStatus();
    return complianceStatus !== 'blocked' && selectedAsset && assignedTo && location && expectedReturnDate;
  };

  const handleCheckOut = async () => {
    if (!selectedAsset || !canProceed()) return;

    try {
      setLoading(true);
      const request: CheckOutRequest = {
        assetId: selectedAsset.id,
        assignedTo,
        checkOutDate: new Date().toISOString(),
        location,
        locationType,
        expectedReturnDate: expectedReturnDate.toISOString(),
        notes,
        accessories,
        overrideCompliance: getComplianceStatus() === 'warning',
      };

      await checkInOutService.checkOutAsset(request);
      toast.success(`${selectedAsset.name} checked out successfully`);
      navigate('/checkinout');
    } catch (error) {
      console.error('Check-out failed:', error);
      toast.error('Failed to check out asset');
    } finally {
      setLoading(false);
      setShowConfirmDialog(false);
    }
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center space-x-2 mb-6">
      {[1, 2, 3].map((s) => (
        <div key={s} className="flex items-center">
          <div
            className={cn(
              'flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium',
              step >= s
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground'
            )}
          >
            {s}
          </div>
          {s < 3 && (
            <div
              className={cn(
                'h-0.5 w-12 mx-2',
                step > s ? 'bg-primary' : 'bg-muted'
              )}
            />
          )}
        </div>
      ))}
    </div>
  );

  const renderStep1 = () => (
    <Card>
      <CardHeader>
        <CardTitle>Select Asset</CardTitle>
        <CardDescription>
          Search for an asset or scan its QR code to begin check-out
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, tag, or category..."
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
          <Button variant="outline" size="icon">
            <QrCode className="h-4 w-4" />
          </Button>
        </div>

        <div className="space-y-2 max-h-96 overflow-y-auto">
          {availableAssets.map((asset) => (
            <Card
              key={asset.id}
              className="cursor-pointer hover:bg-accent transition-colors"
              onClick={() => handleAssetSelect(asset)}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4 text-muted-foreground" />
                      <h4 className="font-medium">{asset.name}</h4>
                      <Badge variant="outline">{asset.category}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">ID: {asset.code}</p>
                    <p className="text-sm text-muted-foreground">
                      Location: {asset.location}
                    </p>
                  </div>
                  <Badge variant="secondary" className="bg-green-100 text-green-800">
                    Available
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))}
          {availableAssets.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              No available assets found. Try adjusting your search.
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );

  const renderStep2 = () => (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Assignment Details</CardTitle>
          <CardDescription>
            Specify who will use the asset and where it will be located
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Selected Asset Summary */}
          <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
            <Package className="h-5 w-5 text-muted-foreground" />
            <div>
              <p className="font-medium">{selectedAsset?.name}</p>
                        <p className="text-sm text-muted-foreground">{selectedAsset?.code}</p>
                      </div>
                    </div>

                    <Separator />

          {/* Assign To */}
          <div className="space-y-2">
            <Label>Assign To</Label>
            <div className="flex gap-2">
              <Select value={assignedToType} onValueChange={(v: any) => setAssignedToType(v)}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="team">Team</SelectItem>
                </SelectContent>
              </Select>
              <Select value={assignedTo} onValueChange={setAssignedTo}>
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder={`Select ${assignedToType}...`} />
                </SelectTrigger>
                <SelectContent>
                  {users.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4" />
                        {user.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Location */}
          <div className="space-y-2">
            <Label>Location</Label>
            <div className="flex gap-2">
              <Select value={locationType} onValueChange={(v: any) => setLocationType(v)}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="site">Site</SelectItem>
                  <SelectItem value="remote">Remote</SelectItem>
                </SelectContent>
              </Select>
              {locationType === 'site' ? (
                <Select value={location} onValueChange={setLocation}>
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Select site..." />
                  </SelectTrigger>
                  <SelectContent>
                    {sites.map((site) => (
                      <SelectItem key={site.id} value={site.id}>
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4" />
                          {site.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  placeholder="Enter remote location..."
                  value={location}
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setLocation(e.target.value)}
                  className="flex-1"
                />
              )}
            </div>
          </div>

          {/* Expected Return Date */}
          <div className="space-y-2">
            <Label>Expected Return Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    'w-full justify-start text-left font-normal',
                    !expectedReturnDate && 'text-muted-foreground'
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {expectedReturnDate ? format(expectedReturnDate, 'PPP') : 'Pick a date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={expectedReturnDate}
                  onSelect={(date) => date && setExpectedReturnDate(date)}
                  disabled={(date) => date < new Date()}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label>Notes (Optional)</Label>
            <Textarea
              placeholder="Add any relevant notes about this check-out..."
              value={notes}
              onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setNotes(e.target.value)}
              rows={3}
            />
          </div>

          {/* Accessories */}
          <div className="space-y-2">
            <Label>Accessories (Optional)</Label>
            <div className="flex gap-2">
              <Input
                placeholder="Add accessory (e.g., charger, keys)..."
                value={newAccessory}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setNewAccessory(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddAccessory()}
              />
              <Button type="button" variant="outline" size="icon" onClick={handleAddAccessory}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            {accessories.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {accessories.map((acc, idx) => (
                  <Badge key={`accessory-${acc.name}-${idx}`} variant="secondary" className="gap-1">
                    {acc.name} ({acc.quantity})
                    <X
                      className="h-3 w-3 cursor-pointer"
                      onClick={() => handleRemoveAccessory(idx)}
                    />
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <Button variant="outline" onClick={() => setStep(1)}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <Button onClick={() => setStep(3)} disabled={!assignedTo || !location}>
          Continue to Review
        </Button>
      </div>
    </div>
  );

  const renderStep3 = () => {
    const complianceStatus = getComplianceStatus();
    
    return (
      <div className="space-y-4">
        {/* Compliance Checks */}
        {complianceChecks.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {complianceStatus === 'ok' && <CheckCircle2 className="h-5 w-5 text-green-600" />}
                {complianceStatus === 'warning' && <AlertTriangle className="h-5 w-5 text-amber-600" />}
                {complianceStatus === 'blocked' && <XCircle className="h-5 w-5 text-red-600" />}
                Compliance Checks
              </CardTitle>
              <CardDescription>
                {complianceStatus === 'ok' && 'All compliance checks passed'}
                {complianceStatus === 'warning' && 'Some warnings detected - review before proceeding'}
                {complianceStatus === 'blocked' && 'Critical issues detected - cannot proceed'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {complianceChecks.map((check) => (
                <div
                  key={check.id}
                  className={cn(
                    'flex items-start gap-3 p-3 rounded-lg',
                    check.status === 'pass' && 'bg-green-50',
                    check.status === 'warning' && 'bg-amber-50',
                    check.status === 'fail' && 'bg-red-50'
                  )}
                >
                  {check.status === 'pass' && <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />}
                  {check.status === 'warning' && <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />}
                  {check.status === 'fail' && <XCircle className="h-5 w-5 text-red-600 mt-0.5" />}
                  <div className="flex-1">
                    <p className="font-medium">{check.name}</p>
                    {check.message && (
                      <p className="text-sm text-muted-foreground mt-1">{check.message}</p>
                    )}
                  </div>
                  {check.blocking && (
                    <Badge variant="destructive" className="text-xs">Blocking</Badge>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Summary */}
        <Card>
          <CardHeader>
            <CardTitle>Review & Confirm</CardTitle>
            <CardDescription>
              Please review the details before checking out this asset
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-muted-foreground">Asset</Label>
                <p className="font-medium">{selectedAsset?.name}</p>
                              <p className="text-sm text-muted-foreground">{selectedAsset?.code}</p>
                            </div>
                            <div>
                              <Label className="text-muted-foreground">Category</Label>
                <p className="font-medium">{selectedAsset?.category}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Assigned To</Label>
                <p className="font-medium">
                  {users.find(u => u.id === assignedTo)?.name || assignedTo}
                </p>
                <p className="text-sm text-muted-foreground capitalize">{assignedToType}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Location</Label>
                <p className="font-medium">
                  {locationType === 'site'
                    ? sites.find(s => s.id === location)?.name || location
                    : location}
                </p>
                <p className="text-sm text-muted-foreground capitalize">{locationType}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Expected Return</Label>
                <p className="font-medium">{format(expectedReturnDate, 'PPP')}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Duration</Label>
                <p className="font-medium">
                  {Math.ceil((expectedReturnDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))} days
                </p>
              </div>
            </div>

            {notes && (
              <>
                <Separator />
                <div>
                  <Label className="text-muted-foreground">Notes</Label>
                  <p className="mt-1">{notes}</p>
                </div>
              </>
            )}

            {accessories.length > 0 && (
              <>
                <Separator />
                <div>
                  <Label className="text-muted-foreground">Accessories</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {accessories.map((acc, idx) => (
                      <Badge key={`review-accessory-${acc.name}-${idx}`} variant="secondary">{acc.name} ({acc.quantity})</Badge>
                    ))}
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <div className="flex justify-between">
          <Button variant="outline" onClick={() => setStep(2)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <Button
            onClick={() => setShowConfirmDialog(true)}
            disabled={!canProceed() || loading}
          >
            {loading ? 'Processing...' : 'Confirm Check-Out'}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="border-b bg-card p-6">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
                      <Package className="h-6 w-6 text-primary" />
                      Check Out Asset
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                      Process asset check-out to employees or departments
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <EvoFeatureFeedbackCollector feature="Asset Check-Out" />
                    <Button variant="outline" onClick={() => navigate('/checkinout')}>
                      <ArrowLeft className="mr-2 h-4 w-4" />
                      Back
                    </Button>
                  </div>
                </div>
              </div>

            <div className="flex-1 space-y-6 p-6">
      {/* Step Indicator */}
      <div className="text-center">
        <p className="text-sm text-muted-foreground">
          Step {step} of 3: {getStepLabel(step)}
        </p>
      </div>

      {renderStepIndicator()}

      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
      {step === 3 && renderStep3()}

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Check-Out</DialogTitle>
            <DialogDescription>
              Are you sure you want to check out {selectedAsset?.name} to{' '}
              {users.find(u => u.id === assignedTo)?.name}?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCheckOut} disabled={loading}>
              {loading ? 'Processing...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}
